import { AitRowGroupData } from "./AitRowGroupData";
import {
  AitHeaderGroupData,
  AitLocationBodyRow,
  AitLocationHeaderRow,
  AitTablePosition,
} from "./interface";

/**
 * Remove a row from either the header or a body row group.
 *
 * For header rows the function adjusts any affected `rowSpan` and `colSpan`
 * values to maintain table consistency. For body rows the matching row is
 * removed from the specified row group. When a header has no rows left the
 * function will return `headerData: false`.
 *
 * @template T - The cell payload type
 * @param headerData - Header group data or `false` when headers are not present
 * @param bodyData - Array of body row groups
 * @param location - Location describing the row to remove (headerRow or bodyRow)
 * @returns An object with updated `headerData` and/or `bodyData` when removal occurred, or an empty object when no change was necessary
 */
export const trRemoveRow = <T>(
  headerData: AitHeaderGroupData<T> | false,
  bodyData: AitRowGroupData<T>[],
  location: AitLocationHeaderRow | AitLocationBodyRow,
): {
  headerData?: AitHeaderGroupData<T> | false;
  bodyData?: AitRowGroupData<T>[];
} => {
  if (
    location.position === AitTablePosition.headerRow &&
    headerData !== false &&
    headerData.rows.findIndex((row) => row.aitid === location.rowId) > -1
  ) {
    const ri = headerData.rows.findIndex((row) => row.aitid === location.rowId);
    const newRows = [...headerData.rows];

    // Check cells for rowSpan
    newRows[ri].cells.forEach((c, ci) => {
      // Found hidden cell
      if (c.rowSpan > 1) {
        // Adjust the rowSpan of the cells below
        for (let i = 1; i < c.rowSpan; i++) newRows[ri + i].cells[ci].rowSpan = 1;
      }
      // Found hidden cell
      if (c.rowSpan === 0) {
        let riUp = 1;
        let found = false;
        // Adjust the rowSpan of the cell above
        while (!found && riUp <= ri) {
          /* istanbul ignore else */
          if (newRows[ri - riUp].cells[ci].rowSpan > 1) {
            newRows[ri - riUp].cells[ci].rowSpan = newRows[ri - riUp].cells[ci].rowSpan - 1;
            found = true;
          }
          riUp++;
        }
      }
    });

    // Remove the row
    newRows.splice(ri, 1);

    // Check that the bottom row has no colSpan
    if (ri === newRows.length && ri > 0) {
      newRows[ri - 1].cells = newRows[ri - 1].cells.map((c) => {
        c.colSpan = 1;
        return c;
      });
    }

    return {
      headerData:
        newRows.length === 0
          ? false
          : {
              ...headerData,
              rows: newRows,
            },
    };
  }

  // Remove body row
  else if (
    location.position === AitTablePosition.bodyRow &&
    bodyData[location.rowGroup].rows.findIndex((row) => row.aitid === location.rowId) > -1
  ) {
    const ri = bodyData[location.rowGroup].rows.findIndex((row) => row.aitid === location.rowId);
    const newBody = [...bodyData];
    const newRows = [...newBody[location.rowGroup].rows];
    newRows.splice(ri, 1);
    newBody[location.rowGroup] = { ...bodyData[location.rowGroup], rows: newRows };
    return { bodyData: newBody };
  }
  return {};
};
